/**
 * Product-sync settings + SKU derivation (Phase 65 / BLU-594 PR1).
 *
 * A per-org JSONB doc (`org_product_sync_settings.settings`) that maps the
 * operator's Shopify catalogue vocabulary — product type names, colour option
 * values, size option values — onto the short codes ThreadHeads' SKU grammar
 * expects. `deriveVariantSku` is the pure function that turns one Shopify
 * variant (plus a minted design code) into the SKU the ERP will store,
 * mirroring the grammar documented in `sync-and-product-architecture.md`:
 *
 *   `[2]{PRODUCT_CODE}[-{SIZE_CODE}]-{COLOUR_CODE}-{DESIGNCODE}{L|D}`
 *
 * - The `2` prefix is glued (no dash) to the product code for dual-sided
 *   (front+back) designs — mirrors the existing `2TEE`/`2HD` grammar.
 * - The size segment is OMITTED for product types in `singleSizeTypes`
 *   (e.g. a one-size tote bag) — mirrors `TB` having no size dimension in
 *   `design-shape-map.ts`.
 * - The trailing `L`/`D` (light/dark print set) is glued directly onto the
 *   design code, chosen from `darkColours` membership.
 *
 * PURE — no I/O. `normalizeProductSyncSettings` is the tolerant-parse /
 * output-safety gate for the org-supplied JSONB doc (mirrors
 * `sanitizeRipRoutingMap` / `resolveDesignShapeCode`'s defense-in-depth
 * posture): bounded entry counts, trimmed names, uppercase codes re-validated
 * against `CODE_RE` (`PRODUCT_TYPE_CODE_RE` for product types) on every parse,
 * so a direct DB/PostgREST write bypassing the tRPC write-path zod can't poison
 * this beyond a dropped leaf.
 */
/** Colour / size CODE shape (and `colourlessCode`): stored uppercase, 1-6 alphanumerics. */
export const CODE_RE = /^[A-Z0-9]{1,6}$/;
/**
 * Product type CODE shape: stored uppercase, 1-10 alphanumerics (sizes and
 * colours stay at 6, `CODE_RE`). LOCKSTEP: product_sync_append_codes and
 * base_garment_codes_code_check.
 */
export const PRODUCT_TYPE_CODE_RE = /^[A-Z0-9]{1,10}$/;
/** Shopify variant option NAMES this module treats as the colour dimension (case-insensitive). */
export const COLOUR_OPTION_NAMES = ['colour', 'color', 'accent'];
/** Shopify variant option NAMES this module treats as the size dimension (case-insensitive). */
export const SIZE_OPTION_NAMES = ['size', 'sizes', 'fit', 'dimensions'];
/**
 * Shopify variant option NAMES this module treats as NEITHER colour nor size
 * nor an error — Shopify's synthetic single-variant option (`Title`, always
 * valued `Default Title`) declared on every product with no real option
 * dimensions. Never counted as an unrecognised option.
 */
export const IGNORED_OPTION_NAMES = ['title'];
/**
 * Whose blank garments a 3PL client prints on, chosen once in the client
 * onboarding wizard: `own` = the client's own blanks (code tables, blank
 * products and blank stock to set up), `supply` = the 3PL's garments (picked
 * per product in Product sync, so those three steps are not needed), `both` =
 * some products on each (the client's own blanks still need those steps).
 */
export const BLANK_SOURCES = ['own', 'supply', 'both'];
/**
 * What a 3PL client sells, chosen once in the client onboarding wizard
 * (BLU-1373): `readyMade` = designs added once and printed on every order,
 * `custom` = each order brings its own artwork (every product is custom),
 * `both` = some of each, decided per product type (and per product for a
 * Mixed type). See `productTypeKind`.
 */
export const SELLS_KINDS = ['readyMade', 'custom', 'both'];
/** A Shopify product GID, the only shape `customProductIds` keeps. */
export const SHOPIFY_PRODUCT_GID_RE = /^gid:\/\/shopify\/Product\/\d{1,20}$/;
/**
 * The most products one org can switch to Custom inside Mixed types
 * (`customProductIds`). Equal to `MAX_PRODUCTS_PER_JOB`.
 */
export const MAX_CUSTOM_PRODUCT_IDS = 500;
/** Phase 91 (BLU-1250): the most product types one org can exclude from print file reuse. */
export const MAX_PRINT_FILE_REUSE_EXCLUDED_TYPES = 50;
/** The org's print file reuse exclude list (absent = none). */
export function printFileReuseExcludedTypesOf(settings) {
    return settings?.printFileReuseExcludedTypes ?? [];
}
/**
 * Normalize the print file reuse exclude list: product type codes trimmed,
 * uppercased, deduped, each matching `PRODUCT_TYPE_CODE_RE`, at most
 * `MAX_PRINT_FILE_REUSE_EXCLUDED_TYPES` (the first ones kept). Anything that
 * is not an array, and every bad entry, is dropped.
 */
export function normalizePrintFileReuseExcludedTypes(raw) {
    const out = [];
    if (!Array.isArray(raw))
        return out;
    const seen = new Set();
    for (const entry of raw) {
        if (out.length >= MAX_PRINT_FILE_REUSE_EXCLUDED_TYPES)
            break;
        if (typeof entry !== 'string')
            continue;
        const code = entry.trim().toUpperCase();
        if (!PRODUCT_TYPE_CODE_RE.test(code) || seen.has(code))
            continue;
        seen.add(code);
        out.push(code);
    }
    return out;
}
/** Phase 86 (R3-3): the two design ID sources. Names fixed (the 86-21 backfill reads them raw). */
export const DESIGN_ID_SOURCES = ['generate', 'metafield'];
/** A string that parses as a date (the switch-on stamp's only shape check). */
function isIsoTimestamp(value) {
    return typeof value === 'string' && value.length <= 40 && !Number.isNaN(Date.parse(value));
}
/**
 * True when the org uses its own design IDs (BYO, R3-3). The ONE switch for
 * the BYO reader, the BYO catalogue fields and the BYO-only `M` letter.
 */
export function byoDesignIdsOn(settings) {
    return settings?.designIdSource === 'metafield';
}
/**
 * Seed size table — identity for adult sizing, folded to the 2-char SKU size
 * codes for the plus range, identity for kids numeric sizing. Re-keyed from
 * the same source as `DEFAULT_DESIGN_SHAPE_MAP`'s size folding
 * (`2XL`→`2X`, `3XL`→`3X`, `4XL`→`4X`).
 */
export const DEFAULT_SIZE_CODES = {
    XS: 'XS',
    S: 'S',
    M: 'M',
    L: 'L',
    XL: 'XL',
    '2XL': '2X',
    '3XL': '3X',
    '4XL': '4X',
    '4': '4',
    '6': '6',
    '8': '8',
    '10': '10',
    '12': '12',
    '14': '14',
    '16': '16',
};
/** Default placeholder colour code (`colourlessCode`) for an org with no saved value / an invalid saved value. */
export const DEFAULT_COLOURLESS_CODE = 'NC';
/**
 * The most products one product sync job takes (a derived Sync or a store SKU
 * Set up), and so the most one "select all matching" returns. The ONE
 * definition: the backend job schemas and the matching-ids scan, and the
 * Products page's run buttons, all import it.
 *
 * 500 (raised from 100 for MZ-scale onboarding runs) is safe because planning
 * and execution are fire-and-forget async (no HTTP request spans the work),
 * the by-ids catalogue re-fetch chunks at 25 ids per call, and the job row's
 * plan JSONB is excluded from the 2 s poll and list selects.
 */
export const MAX_PRODUCTS_PER_JOB = 500;
/**
 * The settings shape for an org with no saved row. `sizes` is pre-seeded with
 * the near-universal default table (`DEFAULT_SIZE_CODES`) so a brand-new org
 * gets sane size matching immediately; `productTypes`/`colours` are always
 * org-specific catalogue vocabulary and cannot be defaulted. `colourlessCode`
 * is likewise universally defaultable (unlike productTypes/colours/sizes,
 * it isn't catalogue-specific vocabulary — see `normalizeProductSyncSettings`).
 */
export const EMPTY_PRODUCT_SYNC_SETTINGS = {
    productTypes: {},
    colours: {},
    sizes: { ...DEFAULT_SIZE_CODES },
    darkColours: [],
    singleSizeTypes: [],
    customTypes: [],
    colourlessCode: DEFAULT_COLOURLESS_CODE,
    syncTag: '',
};
/**
 * The most entries one code table (`productTypes`, `colours`, `sizes`) and one
 * name list (`darkColours`, `singleSizeTypes`, `customTypes`, `mixedTypes`)
 * holds. The ONE definition: the normalizer, the settings router's zod schemas
 * and `BASE_GARMENT_CODE_TABLE_MAX` all use it.
 *
 * LOCKSTEP: the SQL writers `product_sync_append_codes` and
 * `base_garment_adopt_codes` hard-code the same number (latest definitions in
 * `20261016131700_blu1437_code_table_cap_1000.sql`), checked by
 * `code-table-cap-lockstep.test.ts`. SQL counts every stored key, so it is
 * never looser than this normalizer.
 */
export const CODE_TABLE_MAX_ENTRIES = 1000;
const MAX_NAME_LENGTH = 64;
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** Case-insensitive, trim-tolerant name equality (mirrors how Shopify labels get re-typed by admins). */
function namesMatch(a, b) {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}
/**
 * Case-insensitive lookup of a settings code map by name. An O(n) scan over a
 * map of at most `CODE_TABLE_MAX_ENTRIES`; hot loops use `prepareCodeMap`
 * instead; avoids requiring exact-case parity
 * between what an admin typed in Settings and what Shopify returns.
 */
export function matchSettingsCode(map, name) {
    const trimmed = name.trim();
    if (!trimmed)
        return null;
    for (const [key, code] of Object.entries(map)) {
        if (namesMatch(key, trimmed))
            return code;
    }
    return null;
}
/**
 * Index a code map for `matchPreparedCode`. Walks the entries in
 * `matchSettingsCode`'s order and keeps the FIRST entry when two names
 * collide case-insensitively, so every lookup returns exactly what
 * `matchSettingsCode` would.
 */
export function prepareCodeMap(map) {
    const prepared = new Map();
    for (const [key, code] of Object.entries(map)) {
        const name = key.trim().toLowerCase();
        if (!prepared.has(name))
            prepared.set(name, code);
    }
    return prepared;
}
/** `matchSettingsCode` against a `prepareCodeMap` index: one hash lookup instead of a scan. */
export function matchPreparedCode(prepared, name) {
    const trimmed = name.trim();
    if (!trimmed)
        return null;
    return prepared.get(trimmed.toLowerCase()) ?? null;
}
/**
 * Index `settings`' code tables once for a page or scan of lookups, from the
 * same settings object the lookups run against. Never cache it across
 * settings objects.
 */
export function prepareSettingsCodeLookup(settings) {
    return {
        productTypes: prepareCodeMap(settings.productTypes),
        colours: prepareCodeMap(settings.colours),
        sizes: prepareCodeMap(settings.sizes),
    };
}
/**
 * Case-insensitive membership check for `darkColours` / `singleSizeTypes`.
 * Exported so callers outside this module (the preview/coverage handlers'
 * per-colour pattern preview) share the same match semantics instead of
 * duplicating an inline `.some(...toLowerCase...)`.
 */
export function nameListIncludes(list, name) {
    const trimmed = name.trim();
    if (!trimmed)
        return false;
    return list.some((entry) => namesMatch(entry, trimmed));
}
/** `Object.hasOwn` without the ES2022 lib (the frontend compiles against ES2020). */
function hasOwnKey(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
}
function normalizeCodeMapWithCut(raw, codeRe = CODE_RE) {
    const out = {};
    if (!isPlainObject(raw))
        return { value: out, cut: 0 };
    // `count` is exactly `Object.keys(out).length` (O(1) instead of a copy per
    // entry): it moves only when an assignment created a new own key. That
    // keeps today's edge cases: a name repeated after trim overwrites, and
    // `out['__proto__'] = code` creates no key at all (a string is ignored).
    let count = 0;
    const cutNames = new Set();
    for (const [rawName, rawCode] of Object.entries(raw)) {
        const name = typeof rawName === 'string' ? rawName.trim() : '';
        if (!name || name.length > MAX_NAME_LENGTH)
            continue;
        if (typeof rawCode !== 'string')
            continue;
        const code = rawCode.trim().toUpperCase();
        if (!codeRe.test(code))
            continue;
        if (count >= CODE_TABLE_MAX_ENTRIES) {
            // Past the cap nothing is stored (as before), but a valid name that is
            // not already kept is counted, so a caller can tell "the cap cut real
            // entries" apart from "some stored entries were invalid".
            if (name !== '__proto__' && !hasOwnKey(out, name))
                cutNames.add(name);
            continue;
        }
        const had = hasOwnKey(out, name);
        out[name] = code;
        if (!had && hasOwnKey(out, name))
            count += 1;
    }
    return { value: out, cut: cutNames.size };
}
/**
 * Normalize `colourlessCode`: valid uppercase `CODE_RE` string passes through
 * (case-insensitive input, mirrors `normalizeCodeMapWithCut`'s per-code check);
 * absent/wrong-type/invalid-shape falls back to `DEFAULT_COLOURLESS_CODE`
 * ('NC') rather than dropping the key — unlike `productTypes`/`colours`,
 * this is a universal default, not per-org catalogue vocabulary, so there is
 * always a safe value to fall back to.
 */
function normalizeColourlessCode(raw) {
    if (typeof raw !== 'string')
        return DEFAULT_COLOURLESS_CODE;
    const code = raw.trim().toUpperCase();
    return CODE_RE.test(code) ? code : DEFAULT_COLOURLESS_CODE;
}
/**
 * Normalize `syncTag`: a plain Shopify tag string — trimmed, bounded to
 * `MAX_NAME_LENGTH`, with the characters that could escape a quoted
 * search-DSL phrase clause stripped (`"`, `(`, `)`, `:`, `<`, `>`, `*`) —
 * the same character class `sanitizeSearchTerm` strips backend-side
 * (defense-in-depth: the query builder re-sanitizes on every use). Anything
 * absent/wrong-type/over-long/empty-after-strip falls back to `''` (= no
 * tag gate).
 */
function normalizeSyncTag(raw) {
    if (typeof raw !== 'string')
        return '';
    const tag = raw.replace(/["():<>*]/g, '').replace(/\s+/g, ' ').trim();
    if (!tag || tag.length > MAX_NAME_LENGTH)
        return '';
    return tag;
}
/** Normalize `blankSource`: exactly one of `BLANK_SOURCES`, else undefined (key omitted). */
function normalizeBlankSource(raw) {
    return BLANK_SOURCES.includes(raw) ? raw : undefined;
}
/** Normalize `sells`: exactly one of `SELLS_KINDS`, else undefined (key omitted). */
function normalizeSells(raw) {
    return SELLS_KINDS.includes(raw) ? raw : undefined;
}
/** Normalize `customProductIds`: Shopify product GIDs only, deduped, capped at `MAX_CUSTOM_PRODUCT_IDS`. */
function normalizeProductIdList(raw) {
    const out = [];
    if (!Array.isArray(raw))
        return out;
    const seen = new Set();
    for (const entry of raw) {
        if (out.length >= MAX_CUSTOM_PRODUCT_IDS)
            break;
        if (typeof entry !== 'string')
            continue;
        const id = entry.trim();
        if (!SHOPIFY_PRODUCT_GID_RE.test(id) || seen.has(id))
            continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}
function normalizeNameListWithCut(raw) {
    const out = [];
    if (!Array.isArray(raw))
        return { value: out, cut: 0 };
    const seen = new Set();
    let cut = 0;
    for (const entry of raw) {
        if (typeof entry !== 'string')
            continue;
        const name = entry.trim();
        if (!name || name.length > MAX_NAME_LENGTH)
            continue;
        const key = name.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        // Past the cap: not kept (as before), but counted as a valid, new name.
        if (out.length >= CODE_TABLE_MAX_ENTRIES) {
            cut += 1;
            continue;
        }
        out.push(name);
    }
    return { value: out, cut };
}
/**
 * Tolerant parse of the raw stored (or admin-submitted) JSONB doc into a safe
 * `ProductSyncSettings`. Drops anything malformed rather than throwing: a non-
 * object, a non-string code, a code failing `CODE_RE` (`PRODUCT_TYPE_CODE_RE`
 * for product types), or an over-long name are excluded (not upserted, not
 * returned). Bounded to `CODE_TABLE_MAX_ENTRIES` (1,000) per table and name
 * list and `MAX_NAME_LENGTH` (64) per name, so a hostile/corrupted doc can't
 * force an unbounded settings object. Entries past the cap are left out too;
 * use `normalizeProductSyncSettingsWithReport` where that must not go unseen.
 *
 * Non-object input (including `null`/arrays) yields an all-empty settings
 * object — NOT `EMPTY_PRODUCT_SYNC_SETTINGS` (no default size seeding here;
 * that seeding is the caller's "no row at all" fallback, see
 * `preview.handler.ts`). `colourlessCode` is the one exception: it always
 * normalizes to a valid value (default `NC`) regardless of input shape — see
 * `normalizeColourlessCode`.
 */
export function normalizeProductSyncSettings(raw) {
    return normalizeProductSyncSettingsWithReport(raw).settings;
}
/**
 * `normalizeProductSyncSettings`, plus which tables the cap cut. `overCap`
 * lists only fields where at least one valid, new entry was left out because
 * the table already held `CODE_TABLE_MAX_ENTRIES`; invalid or duplicate
 * entries never count, at any table size. Empty means nothing valid was lost.
 */
export function normalizeProductSyncSettingsWithReport(raw) {
    if (!isPlainObject(raw)) {
        return {
            settings: {
                productTypes: {},
                colours: {},
                sizes: {},
                darkColours: [],
                singleSizeTypes: [],
                customTypes: [],
                colourlessCode: DEFAULT_COLOURLESS_CODE,
                syncTag: '',
            },
            overCap: [],
        };
    }
    const productTypes = normalizeCodeMapWithCut(raw.productTypes, PRODUCT_TYPE_CODE_RE);
    const colours = normalizeCodeMapWithCut(raw.colours);
    const sizes = normalizeCodeMapWithCut(raw.sizes);
    const darkColours = normalizeNameListWithCut(raw.darkColours);
    const singleSizeTypes = normalizeNameListWithCut(raw.singleSizeTypes);
    const customTypes = normalizeNameListWithCut(raw.customTypes);
    const mixedTypesResult = normalizeNameListWithCut(raw.mixedTypes);
    const blankSource = normalizeBlankSource(raw.blankSource);
    const sells = normalizeSells(raw.sells);
    const mixedTypes = mixedTypesResult.value;
    const customProductIds = normalizeProductIdList(raw.customProductIds);
    const printFileReuseExcludedTypes = normalizePrintFileReuseExcludedTypes(raw.printFileReuseExcludedTypes);
    const overCap = [];
    const fields = [
        ['productTypes', productTypes.cut],
        ['colours', colours.cut],
        ['sizes', sizes.cut],
        ['darkColours', darkColours.cut],
        ['singleSizeTypes', singleSizeTypes.cut],
        ['customTypes', customTypes.cut],
        ['mixedTypes', mixedTypesResult.cut],
    ];
    for (const [field, cut] of fields) {
        if (cut > 0)
            overCap.push({ field, cut });
    }
    return {
        settings: {
            productTypes: productTypes.value,
            colours: colours.value,
            sizes: sizes.value,
            darkColours: darkColours.value,
            singleSizeTypes: singleSizeTypes.value,
            customTypes: customTypes.value,
            colourlessCode: normalizeColourlessCode(raw.colourlessCode),
            syncTag: normalizeSyncTag(raw.syncTag),
            ...(blankSource ? { blankSource } : {}),
            // BLU-1373: each new key only when meaningful, so an org without them
            // normalizes to exactly the 8 legacy keys.
            ...(sells ? { sells } : {}),
            ...(mixedTypes.length > 0 ? { mixedTypes } : {}),
            ...(customProductIds.length > 0 ? { customProductIds } : {}),
            // Phase 86 (R3-3): only `metafield` is kept; absent means generate.
            ...(raw.designIdSource === 'metafield' ? { designIdSource: 'metafield' } : {}),
            ...(raw.designIdSource === 'metafield' && isIsoTimestamp(raw.designIdSourceSwitchedOnAt)
                ? { designIdSourceSwitchedOnAt: raw.designIdSourceSwitchedOnAt }
                : {}),
            // Phase 91 (BLU-1250): omitted when empty, like the BLU-1373 keys.
            ...(printFileReuseExcludedTypes.length > 0 ? { printFileReuseExcludedTypes } : {}),
        },
        overCap,
    };
}
/**
 * How a product type is sold: the ONE rule every reader of the custom
 * decision goes through (BLU-1373). Precedence:
 *   1. `sells === 'both'` AND the type is in `mixedTypes` -> `mixed`;
 *   2. the type is in `customTypes` -> `custom` (also after answering
 *      Ready-made: the answer never clears a per-type Custom mark);
 *   3. `sells === 'custom'` -> `custom` (the Custom-only default, also for a
 *      blank or unlisted type);
 *   4. else `readyMade`.
 * Without the new keys this is exactly `nameListIncludes(customTypes, name)`.
 * A product with no type (blank name) is never in a list, so it follows
 * what the client sells: custom under Custom only, ready-made otherwise.
 */
export function productTypeKind(settings, productTypeName) {
    if (settings.sells === 'both' && nameListIncludes(settings.mixedTypes ?? [], productTypeName))
        return 'mixed';
    if (nameListIncludes(settings.customTypes, productTypeName))
        return 'custom';
    if (settings.sells === 'custom')
        return 'custom';
    return 'readyMade';
}
/**
 * Whether one product derives as on-demand custom (the `CUST-` wrapper, no
 * `2` prefix, no platen rules, complete with no designs): its type is
 * `custom`, or its type is `mixed` AND its GID is in `customProductIds`. A
 * product of a Mixed type that is not listed, or with no id, is Ready-made
 * (the Mixed default). Named apart from the production stations'
 * `isCustomProduct` (the unit's SKU starts with CUST), a different question.
 */
export function isCustomSyncProduct(settings, productTypeName, shopifyProductId) {
    const kind = productTypeKind(settings, productTypeName);
    if (kind === 'custom')
        return true;
    if (kind !== 'mixed' || !shopifyProductId)
        return false;
    return (settings.customProductIds ?? []).includes(shopifyProductId);
}
/**
 * Set one type's kind in the two type lists (pure). `custom`: add to
 * `customTypes`, remove from `mixedTypes`; `mixed`: the reverse; `readyMade`:
 * remove from both. Matching is case-insensitive and every other entry is
 * kept in order.
 */
export function applyProductTypeKind(lists, typeName, kind) {
    const name = typeName.trim();
    const without = (list) => list.filter((entry) => !namesMatch(entry, name));
    const customTypes = without(lists.customTypes);
    const mixedTypes = without(lists.mixedTypes ?? []);
    if (name && kind === 'custom')
        customTypes.push(name);
    if (name && kind === 'mixed')
        mixedTypes.push(name);
    return { customTypes, mixedTypes };
}
/** Add (`custom` true) or remove one product GID in `customProductIds` (pure, deduped). */
export function withCustomProductId(ids, productId, custom) {
    const rest = [...new Set(ids)].filter((id) => id !== productId);
    return custom ? [...rest, productId] : rest;
}
function lowerNameSet(list) {
    return new Set((list ?? []).map((name) => name.trim().toLowerCase()).filter(Boolean));
}
function sameSet(a, b) {
    if (a.size !== b.size)
        return false;
    for (const value of a)
        if (!b.has(value))
            return false;
    return true;
}
/**
 * Whether a settings change could change any product's custom decision.
 * CONSERVATIVE: it may say true when no real product would change, and
 * never says false when one could. Used only by the onboarding wizard's
 * confirm before a save re-SKUs a client that may already have synced.
 *   - `sells === 'custom'` on exactly one side: true; on both: false
 *     (everything is custom either way).
 *   - Otherwise it compares, case-insensitively and order-free, the
 *     `customTypes` sets, the effective Mixed sets and the effective id sets.
 *     Effective = the list when that side sells both, else empty (Mixed is
 *     inert outside Both). Any difference: true.
 */
export function customDecisionChanged(before, after) {
    const beforeCustom = before.sells === 'custom';
    const afterCustom = after.sells === 'custom';
    if (beforeCustom !== afterCustom)
        return true;
    if (beforeCustom)
        return false;
    const effectiveMixed = (s) => lowerNameSet(s.sells === 'both' ? s.mixedTypes : []);
    const effectiveIds = (s) => new Set(s.sells === 'both' ? (s.customProductIds ?? []) : []);
    return (!sameSet(lowerNameSet(before.customTypes), lowerNameSet(after.customTypes)) ||
        !sameSet(effectiveMixed(before), effectiveMixed(after)) ||
        !sameSet(effectiveIds(before), effectiveIds(after)));
}
/**
 * Mint a fresh design code suggestion from a product title: uppercase
 * A-Z0-9 slug (first word's first 4 chars + second word's first 3 chars, or
 * the first 6 chars for a single-word title), padded to a 3-char minimum from
 * the title's letters for very short titles, then suffixed with an
 * incrementing counter (`CAPY1`, `CAPY2`, ...) until the result is not already
 * in `existing`. The base is re-truncated on every attempt so the returned
 * code never exceeds 10 characters, however many digits the counter grows to.
 *
 * Pure — does not mutate `existing`; the caller decides whether/when to add
 * the returned code so subsequent titles on the same page dedupe against it.
 */
export function mintDesignCode(title, existing) {
    // Split on WHITESPACE first (word boundaries), THEN strip non-alphanumerics
    // within each word — so an embedded apostrophe/hyphen ("Capy's") doesn't
    // masquerade as an extra word boundary.
    const words = title
        .trim()
        .split(/\s+/)
        .map((w) => w.toUpperCase().replace(/[^A-Z0-9]/g, ''))
        .filter((w) => w.length > 0);
    let base = '';
    if (words.length >= 2) {
        base = `${words[0].slice(0, 4)}${words[1].slice(0, 3)}`;
    }
    else if (words.length === 1) {
        base = words[0].slice(0, 6);
    }
    if (base.length < 3) {
        const letters = words.join('');
        base = letters.length >= 3 ? letters.slice(0, 3) : `${letters}XXX`.slice(0, 3);
    }
    const MAX_LENGTH = 10;
    let n = 1;
    for (;;) {
        const suffix = String(n);
        const truncatedBase = base.slice(0, Math.max(1, MAX_LENGTH - suffix.length));
        const candidate = `${truncatedBase}${suffix}`;
        if (!existing.has(candidate))
            return candidate;
        n++;
    }
}
function lookupCode(input, table, name) {
    return input.codeLookup
        ? matchPreparedCode(input.codeLookup[table], name)
        : matchSettingsCode(input.settings[table], name);
}
/*
 * The three per-dimension checks. `deriveVariantSku` stops at the first gap;
 * `findUnmappedDimensions` reports every gap. Both read these and nothing
 * else, so the two can never disagree about what a gap is.
 */
function checkProductType(input) {
    if (input.productCodeOverride !== undefined) {
        // BLU-1522 (S8): the variant's own base garment code, never the name lookup.
        const override = input.productCodeOverride.trim().toUpperCase();
        return PRODUCT_TYPE_CODE_RE.test(override) ? { ok: true, code: override } : { ok: false, reason: 'unmapped_product_type' };
    }
    const code = lookupCode(input, 'productTypes', input.productTypeName);
    return code ? { ok: true, code } : { ok: false, reason: 'unmapped_product_type' };
}
function checkColour(input) {
    const { colourName, omitColour, settings } = input;
    if (omitColour) {
        const placeholder = settings.colourlessCode.trim().toUpperCase();
        return placeholder && CODE_RE.test(placeholder) ? { ok: true, code: placeholder } : { ok: false, reason: 'missing_colour' };
    }
    const code = lookupCode(input, 'colours', colourName ?? '');
    return code ? { ok: true, code } : { ok: false, reason: 'unmapped_colour' };
}
function checkSize(input) {
    const { sizeName, omitSize } = input;
    if (omitSize)
        return { ok: true, code: null };
    const code = lookupCode(input, 'sizes', sizeName ?? '');
    return code ? { ok: true, code } : { ok: false, reason: 'unmapped_size' };
}
const DIMENSION_CHECKS = [checkProductType, checkColour, checkSize];
/** Which of `DIMENSION_CHECKS` reports each gap. */
const DIMENSION_OF_GAP = {
    unmapped_product_type: 0,
    unmapped_colour: 1,
    missing_colour: 1,
    unmapped_size: 2,
};
/**
 * Derive the SKU grammar `[2]{PRODUCT_CODE}[-{SIZE_CODE}]-{COLOUR_CODE}-{DESIGNCODE}{L|D}`
 * for one Shopify variant. Never throws — an unmapped or missing dimension
 * returns a typed failure reason instead of a partial/garbage SKU.
 *
 * A colour-less product (`omitColour: true`, resolved by the caller from the
 * product declaring NO colour-alias option at all) uses
 * `settings.colourlessCode` in place of a real colour code rather than
 * failing closed — the bridge parses colour POSITIONALLY and RIP routing
 * maps colour→profile, so the segment must always exist. `missing_colour` is
 * now only reachable defensively under `omitColour: true`, if
 * `colourlessCode` itself somehow normalizes to an invalid/empty value. A
 * blank `colourName` on a colour-HAVING product (`omitColour: false`) is a
 * genuine data problem, NOT colour-less — it fails `unmapped_colour` exactly
 * like an unrecognised colour value would (mirrors `omitSize`'s identical
 * treatment of a blank `sizeName`).
 */
export function deriveVariantSku(input) {
    const { productTypeName, colourName, designCode, hasBack, settings } = input;
    const designCodeUpper = designCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{1,10}$/.test(designCodeUpper)) {
        return { ok: false, reason: 'invalid_design_code' };
    }
    // Phase 86 (R3-8): Medium is BYO-only, so no other org can ever mint an `M` SKU.
    const { designSet } = input;
    if (designSet !== undefined && designSet !== 'L' && designSet !== 'D' && !(designSet === 'M' && byoDesignIdsOn(settings))) {
        return { ok: false, reason: 'invalid_design_code' };
    }
    const productType = checkProductType(input);
    if (!productType.ok)
        return { ok: false, reason: productType.reason };
    const colour = checkColour(input);
    if (!colour.ok)
        return { ok: false, reason: colour.reason };
    const size = checkSize(input);
    if (!size.ok)
        return { ok: false, reason: size.reason };
    const productCode = productType.code;
    const colourCode = colour.code;
    const sizeCode = size.code;
    const printSet = designSet ?? (nameListIncludes(settings.darkColours, colourName ?? '') ? 'D' : 'L');
    // On-demand custom types are single-garment in the SKU grammar: the customer's
    // per-order artwork carries front/back, NOT the `2` dual-sided mechanism. So a
    // custom type NEVER gets the `2` prefix — it derives `CUST-TEE-…`, never
    // `CUST-2TEE-…`. Suppressing the `2` at the source keeps the base garment
    // un-leaked (`CUST-2TEE-…` would strip to `2TEE-S-BK`, a non-existent blank)
    // WITHOUT having to touch the load-bearing core unwrap helpers. Whether this
    // product is custom is the ONE shared rule (`isCustomSyncProduct`): its type,
    // what the client sells, and its own switch inside a Mixed type.
    const isCustomType = isCustomSyncProduct(settings, productTypeName, input.shopifyProductId);
    const productSegment = `${hasBack && !isCustomType ? '2' : ''}${productCode}`;
    const segments = [productSegment];
    if (sizeCode)
        segments.push(sizeCode);
    segments.push(colourCode);
    const core = `${segments.join('-')}-${designCodeUpper}${printSet}`;
    // On-demand custom types get the `CUST-` wrapper around the standard SKU
    // (`CUST-{baseCode}-{size}-{colour}-{design}{L|D}`). Intake's `parseSKU` then
    // flags `isCustom`, and `cleanSkuWithRules` strips `CUST` back to the base
    // garment for blank matching. Non-custom types are byte-identical.
    const sku = `${isCustomType ? 'CUST-' : ''}${core}`.toUpperCase();
    // Bare productCode (no `2`, no CUST-), no design segment — see DeriveVariantSkuResult.baseSku.
    const baseSku = [productCode, sizeCode, colourCode].filter(Boolean).join('-').toUpperCase();
    return { ok: true, sku, baseSku, productCode: productCode.toUpperCase(), sizeCode: sizeCode ? sizeCode.toUpperCase() : null };
}
/**
 * Every unmapped dimension, in check order (product type, colour, size);
 * empty when all resolve. Never checks the design code.
 *
 * `firstGap` is `deriveVariantSku`'s failure reason for this same input: the
 * checks up to and including its dimension already ran there, so they are
 * not repeated and it leads the list.
 */
export function findUnmappedDimensions(input, firstGap) {
    const gaps = firstGap ? [firstGap] : [];
    for (let i = firstGap ? DIMENSION_OF_GAP[firstGap] + 1 : 0; i < DIMENSION_CHECKS.length; i++) {
        const check = DIMENSION_CHECKS[i](input);
        if (!check.ok)
            gaps.push(check.reason);
    }
    return gaps;
}
function upperCodeSet(codes) {
    const out = new Set();
    for (const code of codes) {
        if (typeof code === 'string' && code.trim())
            out.add(code.trim().toUpperCase());
    }
    return out;
}
/**
 * Phase 92 (BLU-1269, research Q11): split a 3PL blank's OWN code into its
 * product type, size and colour segments, using the 3PL's own code tables
 * for the first two. A supply product's print SKU is composed from the
 * blank's own segments, not by decoding names through the tables, so this is
 * the check that a blank can carry a supply product at all.
 *
 * Accepted shapes: `{TYPE}-{SIZE}-{COLOUR}` (three segments) and
 * `{TYPE}-{COLOUR}` (two segments, no size). The product type must be one of
 * `productTypeCodes` and the size one of `sizeCodes` (both matched
 * case-insensitively). The colour segment is NEVER checked against a table:
 * real 3PL code tables carry no colours (research Q5/Q11). Any other segment
 * count, or an empty segment, is `shape`. Never throws.
 */
export function splitSupplyBlankCode(code, tables) {
    if (typeof code !== 'string')
        return { ok: false, reason: 'shape' };
    const input = code.trim().toUpperCase();
    if (!input)
        return { ok: false, reason: 'shape' };
    const segments = input.split('-');
    if (segments.length !== 2 && segments.length !== 3)
        return { ok: false, reason: 'shape' };
    if (segments.some((segment) => segment.length === 0))
        return { ok: false, reason: 'shape' };
    const productTypeCode = segments[0];
    if (!upperCodeSet(tables.productTypeCodes).has(productTypeCode)) {
        return { ok: false, reason: 'unknown_product_type' };
    }
    const sizeCode = segments.length === 3 ? segments[1] : null;
    if (sizeCode !== null && !upperCodeSet(tables.sizeCodes).has(sizeCode)) {
        return { ok: false, reason: 'unknown_size' };
    }
    return { ok: true, productTypeCode, sizeCode, colourSegment: segments[segments.length - 1] };
}
/**
 * Phase 92 (SIMP-6): the product-type code of a blank code, the key a 3PL's
 * per-product-type rules are read under: the first `-` segment, trimmed and
 * upper-cased (`'tee-L-BLK'` -> `'TEE'`). `''` for a missing or empty code.
 * Unlike {@link splitSupplyBlankCode} it checks no table and no shape. Pure.
 */
export function supplyBlankProductTypeCode(code) {
    if (typeof code !== 'string')
        return '';
    return (code.split('-')[0] ?? '').trim().toUpperCase();
}
const SUPPLY_BLANK_SEGMENT_RE = /^[A-Z0-9]+$/;
/**
 * Phase 92 (BLU-1265, D-01): a supply product's print SKU, composed from the
 * 3PL blank's OWN code (never decoded through any code table):
 * `[CUST-][2]{blankCode}-{DESIGN}{L|D}`.
 *
 * Same rules as `deriveVariantSku`: the design code is upper-cased and must
 * match `^[A-Z0-9]{1,10}$`; a custom type gets the `CUST-` wrapper and NEVER
 * the `2` (the customer's artwork carries front and back); otherwise a print
 * set with a back gets the `2` prefix. The blank code must have two or three
 * segments of letters and digits, and its product type must not start with
 * `2` or be `CUST` (either would read back as a different garment), so the
 * result always round-trips through the backend's `supplyBaseFromPrintSku`
 * to the blank code. Never throws.
 */
export function composeSupplyVariantSku(input) {
    const blank = typeof input.blankCode === 'string' ? input.blankCode.trim().toUpperCase() : '';
    const segments = blank.split('-');
    if ((segments.length !== 2 && segments.length !== 3) ||
        !segments.every((segment) => SUPPLY_BLANK_SEGMENT_RE.test(segment)) ||
        segments[0].startsWith('2') ||
        segments[0] === 'CUST') {
        return { ok: false, reason: 'invalid_blank_code' };
    }
    const design = typeof input.designCode === 'string' ? input.designCode.trim().toUpperCase() : '';
    if (!/^[A-Z0-9]{1,10}$/.test(design))
        return { ok: false, reason: 'invalid_design_code' };
    const core = `${blank}-${design}${input.isDark ? 'D' : 'L'}`;
    if (input.customType)
        return { ok: true, sku: `CUST-${core}` };
    return { ok: true, sku: `${input.dualPrint ? '2' : ''}${core}` };
}
/**
 * Phase 92 (BLU-1265): match one client store value (a colour or size name)
 * to a blank dimension value. Case-insensitive and trimmed, against the
 * candidate's name OR its code segment. Returns the matched segment, or null
 * when nothing matches or two different segments match (never a guess).
 */
export function matchBlankValue(storeValue, candidates) {
    if (typeof storeValue !== 'string' || !storeValue.trim())
        return null;
    let found = null;
    for (const candidate of candidates) {
        const hit = namesMatch(candidate.segment, storeValue) ||
            (typeof candidate.name === 'string' && candidate.name.trim() !== '' && namesMatch(candidate.name, storeValue));
        if (!hit)
            continue;
        if (found !== null && found !== candidate.segment)
            return null;
        found = candidate.segment;
    }
    return found;
}
const DERIVED_DESIGN_SEGMENT_RE = /^([A-Z0-9]{1,10})([LD])$/;
/** Phase 86 (R3-8): the BYO grammar's design segment, with the Medium letter. Read only when `byoDesignIdsOn(settings)`. */
const BYO_DERIVED_DESIGN_SEGMENT_RE = /^([A-Z0-9]{1,10})([LDM])$/;
/**
 * Recover the product type, size, colour and design codes from a SKU that
 * `deriveVariantSku` produced, using the org's OWN code tables. This is the
 * fallback that recovers identity from a synced SKU when the projection row
 * has no stored size (86-01 item 6, D-22): print time and commit time call it
 * when `org_variant_custom_print_skus.size_code` is null.
 *
 * Grammar: `[2]{PRODUCT}[-{SIZE}]-{COLOUR}-{DESIGN}{L|D}` (`{L|D|M}` for a BYO
 * org only, R3-8). The input is trimmed
 * and uppercased; a `CUST-` SKU is refused (D-22 excludes customs). Every
 * reading (with and without peeling a leading `2`) is checked against the
 * code VALUES in `settings.productTypes`, `settings.sizes`, `settings.colours`
 * and `settings.colourlessCode`, and re-joined with the same grammar; a parse
 * is accepted only when exactly ONE reading validates and its re-join equals
 * the input byte for byte. Anything else returns `null`, which callers treat
 * as "no identity" (today's behaviour). Never throws and never guesses.
 */
export function parseDerivedVariantSku(sku, settings) {
    if (typeof sku !== 'string')
        return null;
    const input = sku.trim().toUpperCase();
    if (!input || input.startsWith('CUST-'))
        return null;
    const productCodes = codeValues(settings.productTypes, PRODUCT_TYPE_CODE_RE);
    const sizeCodes = codeValues(settings.sizes);
    const colourCodes = codeValues(settings.colours);
    const colourless = typeof settings.colourlessCode === 'string' ? settings.colourlessCode.trim().toUpperCase() : '';
    if (colourless && CODE_RE.test(colourless))
        colourCodes.add(colourless);
    // Phase 86 (R3-8): `M` parses only for a BYO org; every other org reads `[LD]` as before.
    const designSegmentRe = byoDesignIdsOn(settings) ? BYO_DERIVED_DESIGN_SEGMENT_RE : DERIVED_DESIGN_SEGMENT_RE;
    const readings = [];
    const candidates = [{ body: input, dualSided: false }];
    if (input.startsWith('2'))
        candidates.push({ body: input.slice(1), dualSided: true });
    for (const { body, dualSided } of candidates) {
        const segments = body.split('-');
        if (segments.length !== 3 && segments.length !== 4)
            continue;
        const designMatch = designSegmentRe.exec(segments[segments.length - 1]);
        if (!designMatch)
            continue;
        const productTypeCode = segments[0];
        const sizeCode = segments.length === 4 ? segments[1] : null;
        const colourCode = segments[segments.length - 2];
        if (!productCodes.has(productTypeCode))
            continue;
        if (sizeCode !== null && !sizeCodes.has(sizeCode))
            continue;
        if (!colourCodes.has(colourCode))
            continue;
        const rejoined = `${dualSided ? '2' : ''}${[productTypeCode, sizeCode, colourCode].filter((s) => s !== null).join('-')}-${designMatch[1]}${designMatch[2]}`;
        if (rejoined !== input)
            continue;
        readings.push({ productTypeCode, sizeCode, colourCode, designCode: designMatch[1], dualSided });
    }
    return readings.length === 1 ? readings[0] : null;
}
/** The org's valid product type code VALUES (uppercase), as `parseDerivedVariantSku` checks them. */
export function productTypeCodeValues(settings) {
    return codeValues(settings.productTypes, PRODUCT_TYPE_CODE_RE);
}
/** The most extra own base garments one product takes beside its default one. */
export const OWN_BLANK_MAX_GROUPS = 5;
/** The most products one org can keep own base garment groups for. */
export const OWN_BLANK_MAX_PRODUCTS = 500;
/** The key a store option value is grouped and looked up by: trimmed, lower case; '' for none. */
export function ownBlankValueKey(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}
function normalizeOwnBlankEntry(raw) {
    const invalid = { invalid: true };
    if (!isPlainObject(raw))
        return invalid;
    const split = raw.split;
    if (split !== 'colour' && split !== 'size')
        return invalid;
    const groups = raw.groups;
    if (!Array.isArray(groups) || groups.length === 0 || groups.length > OWN_BLANK_MAX_GROUPS)
        return invalid;
    const codes = new Set();
    const values = new Set();
    const out = [];
    for (const group of groups) {
        if (!isPlainObject(group) || typeof group.code !== 'string' || !Array.isArray(group.values))
            return invalid;
        const code = group.code.trim().toUpperCase();
        if (!PRODUCT_TYPE_CODE_RE.test(code) || codes.has(code))
            return invalid;
        codes.add(code);
        if (group.values.length === 0)
            return invalid;
        const groupValues = [];
        for (const value of group.values) {
            const key = typeof value === 'string' ? ownBlankValueKey(value) : '';
            if (!key || values.has(key))
                return invalid;
            values.add(key);
            groupValues.push(key);
        }
        out.push({ code, values: groupValues });
    }
    return { split, groups: out };
}
/**
 * The org's stored `own_blank_groups` column, by Shopify product GID. Anything
 * but an object reads as none; an entry that does not read is kept as
 * `{ invalid: true }`. Never throws.
 */
export function normalizeOwnBlankGroups(raw) {
    const out = new Map();
    if (!isPlainObject(raw))
        return out;
    for (const [productId, entry] of Object.entries(raw))
        out.set(productId, normalizeOwnBlankEntry(entry));
    return out;
}
/**
 * The own base garment code a variant takes from its product's entry, or
 * `null` for the product's default garment (no entry, an invalid one, or a
 * value no group names).
 */
export function ownBlankCodeFor(entry, colourName, sizeName) {
    if (!entry || 'invalid' in entry)
        return null;
    const key = ownBlankValueKey(entry.split === 'colour' ? colourName : sizeName);
    if (!key)
        return null;
    return entry.groups.find((g) => g.values.includes(key))?.code ?? null;
}
/** The first product type NAME the org maps to `code`, or null. Names a new blank row for that code. */
export function ownCodeTypeName(code, settings) {
    const wanted = code.trim().toUpperCase();
    if (!isPlainObject(settings.productTypes))
        return null;
    for (const [name, value] of Object.entries(settings.productTypes)) {
        if (typeof value === 'string' && value.trim().toUpperCase() === wanted)
            return name;
    }
    return null;
}
/** A code's blank has no size segment: its first mapped type name is a single-size type. */
export function ownCodeIsSingleSize(code, settings) {
    const name = ownCodeTypeName(code, settings);
    return name !== null && nameListIncludes(settings.singleSizeTypes, name);
}
/** The set of valid uppercase code VALUES in a settings code map (`codeRe`: 10 for product types). */
function codeValues(map, codeRe = CODE_RE) {
    const out = new Set();
    if (!isPlainObject(map))
        return out;
    for (const value of Object.values(map)) {
        if (typeof value !== 'string')
            continue;
        const code = value.trim().toUpperCase();
        if (codeRe.test(code))
            out.add(code);
    }
    return out;
}
